import { SitesLocalePrefix } from '../../../enums';
import { type StationBrand, TypeOfIntegration, FeatureFlags } from '@centric-os/types';
import { useSiteDetails } from '../..';
import { isEmpty, isNil } from 'lodash-es';
import { useTranslation } from '@centric-os/helpers';
import { useSplitIoStore } from '@centric-os/stores/splitio/use-split-io';

type IntegrationCheck = {
  isEnabled: (station: StationBrand, multigroupId: string) => boolean;
  name: string;
};

export default () => {
  // Set station localization
  const { translate } = useTranslation();
  const STATION_PREFIX = SitesLocalePrefix.SITE_STATION;

  // Set site details composable
  const { devices } = useSiteDetails();

  const { isFeatureOn } = useSplitIoStore();

  const isThriveEnabled = (station: StationBrand): boolean => {
    return Boolean(station.meta?.software_integrations?.thrive?.enabled);
  };

  const isBoostEnabled = (station: StationBrand): boolean => {
    return Boolean(station.meta?.software_integrations?.boost?.enabled);
  };

  const isRobotDeliveryEnabled = (station: StationBrand): boolean => {
    const { thrive, boost } = station.meta?.software_integrations ?? {};
    return Boolean(
      (thrive?.enabled && thrive?.robot_delivery) || (boost?.enabled && boost?.robot_delivery),
    );
  };

  const isTacitEnabled = (station: StationBrand, multigroupId: string): boolean => {
    return !station.is?.hidden && multigroupId === import.meta.env.VITE_TACIT_MULTIGROUP;
  };

  const isNextepEnabled = (station: StationBrand): boolean => {
    return Boolean(
      station.meta?.software_integrations?.nextep?.enabled ||
        station.meta?.software_integrations?.nextep?.mobile_app ||
        station.meta?.software_integrations?.nextep?.web,
    );
  };

  const isVolanteEnabled = (station: StationBrand): boolean => {
    return Boolean(station.meta?.software_integrations?.volante?.enabled);
  };

  const isAgilysysEnabled = (station: StationBrand): boolean => {
    return Boolean(station.meta?.software_integrations?.agilysys?.enabled);
  };

  const isTime2EatEnabled = (station: StationBrand): boolean => {
    return station.meta?.software_integrations?.time2eat?.enabled;
  };

  const isMashginEnabled = (station: StationBrand): boolean => {
    return station.meta?.software_integrations?.mashgin?.enabled;
  };

  const hasCDKdsDevice = (station: StationBrand): boolean =>
    devices.value?.some((device) => device.brands?.includes(station.id));

  const isCentricHubEnabled = (station: StationBrand): boolean => {
    return station.meta?.software_integrations?.centric_hub?.enabled;
  };

  const isAgilysysDodEnabled = (station: StationBrand): boolean => {
    return station.meta?.software_integrations?.agilysys_dod?.enabled;
  };

  const isGrubhubEnabled = (station: StationBrand): boolean => {
    return station.meta?.software_integrations?.grubhub?.enabled;
  };

  const isRystEnabled = (station: StationBrand): boolean => {
    return station.meta?.software_integrations?.ryst?.enabled;
  };

  const isIllumiaEnabled = (station: StationBrand): boolean => {
    return station.meta?.software_integrations?.illumia?.enabled;
  };

  const isAmazonEnabled = (station: StationBrand): boolean => {
    return (
      isFeatureOn(FeatureFlags.AMAZON_INTEGRATION) &&
      Boolean(station.meta?.software_integrations?.amazon?.enabled)
    );
  };

  // Integrations shown as station tags, in display order.
  const integrationChecks: IntegrationCheck[] = [
    { isEnabled: isThriveEnabled, name: TypeOfIntegration.thrive.name },
    { isEnabled: isTacitEnabled, name: TypeOfIntegration.tacit.name },
    { isEnabled: isBoostEnabled, name: TypeOfIntegration.boost.name },
    { isEnabled: isNextepEnabled, name: TypeOfIntegration.nextep.name },
    { isEnabled: isVolanteEnabled, name: TypeOfIntegration.volante.name },
    { isEnabled: isAgilysysEnabled, name: TypeOfIntegration.agilysys.name },
    { isEnabled: isMashginEnabled, name: TypeOfIntegration.mashgin.name },
    { isEnabled: isTime2EatEnabled, name: TypeOfIntegration.time2eat.name },
    { isEnabled: hasCDKdsDevice, name: TypeOfIntegration.cdl.name },
    { isEnabled: isCentricHubEnabled, name: TypeOfIntegration.centric_hub.name },
    { isEnabled: isAgilysysDodEnabled, name: TypeOfIntegration.agilysys_dod.name },
    { isEnabled: isGrubhubEnabled, name: TypeOfIntegration.grubhub.name },
    { isEnabled: isRystEnabled, name: TypeOfIntegration.ryst.name },
    { isEnabled: isIllumiaEnabled, name: TypeOfIntegration.illumia.name },
    { isEnabled: isAmazonEnabled, name: TypeOfIntegration.amazon.name },
  ];

  /**
   * Retrieves the available integration types for a given station.
   *
   * @param {StationBrand} station - The station object containing metadata for integrations.
   * @param {string} multigroupId - The multigroup ID of the station.
   * @returns {string[]} An array of integrations available for the station.
   */
  const getIntegrations = (station: StationBrand, multigroupId: string): string[] => {
    if (isNil(station)) return [];

    return integrationChecks
      .filter(({ isEnabled }) => isEnabled(station, multigroupId))
      .map(({ name }) => name);
  };

  /**
   * Checks if the user can toggle the "Show in App" option.
   *
   * If an app belongs to the Thrive or Boost multigroup, the user can choose whether a station is displayable in the app.
   *
   * @param {StationBrand} station - The station object containing metadata for KDS types.
   * @returns {boolean} True if the station is visible and part of Boost, Thrive, or Tacit. False otherwise.
   */
  const canToggleShowInApp = (station: StationBrand, multigroupId: string): boolean => {
    const isThriveOrBoostIntegrationEnabled = !!(
      !isEmpty(station.timeslots) ||
      station.is?.scan_and_go_supported ||
      station.is?.frictionless_supported
    );
    return (
      isThriveOrBoostIntegrationEnabled &&
      [import.meta.env.VITE_THRIVE_MULTIGROUP, import.meta.env.VITE_BOOST_MULTIGROUP].includes(
        multigroupId,
      )
    );
  };

  /**
   * Shared rule for the "Show in App" toggle's disabled state, used by both the Menu Hours
   * list and the Station detail integration tile so they stay in sync.
   */
  const isShowInAppToggleDisabled = (flags: {
    isIntegrated?: boolean;
    canEnable?: boolean;
  }): boolean => !(flags.isIntegrated && flags.canEnable);

  return {
    getIntegrations,
    translate,
    isThriveEnabled,
    isBoostEnabled,
    isRobotDeliveryEnabled,
    isTime2EatEnabled,
    STATION_PREFIX,
    canToggleShowInApp,
    isShowInAppToggleDisabled,
  };
};
